import { useGetEquityPriceSummary } from '#/api/generated/equity/equity';
import { TickerPriceSummary } from '#/components/detail/TickerPriceSummary';
import { useIsBelowFoldInView } from '#/components/index/HomepageBelowFoldSection';
import { NewsCard } from '#/components/ui/NewsCard';
import { SectionHeader } from '#/components/ui/SectionHeader';
import type { HomepageNewsView } from '#/content/homepage';
import { isSuccessResponse } from '#/lib/api-client';
import { getEquityETFPriceChangeDisplay } from '#/utils/priceChange';

interface NewsSectionProps {
  content: HomepageNewsView;
}

/** Editorial can author any number of news items; the homepage shows at most this many. */
const MAX_NEWS_CARDS = 3;

/**
 * Live price summary for a news item's ticker. Renders nothing until (and
 * unless) the price summary resolves.
 */
function NewsTickerSummary({ symbol }: { symbol: string }) {
  const isInView = useIsBelowFoldInView();

  const query = useGetEquityPriceSummary(symbol, {
    query: {
      enabled: isInView,
    },
  });
  const summary = isSuccessResponse(query.data) ? query.data.data : null;

  const { percentChange, valueChange, periodLabel } =
    getEquityETFPriceChangeDisplay(summary);

  const hasPriceData =
    summary != null &&
    (summary.current_price != null ||
      percentChange != null ||
      valueChange != null);

  if (!hasPriceData) {
    return null;
  }

  return (
    <div className="flex">
      <TickerPriceSummary
        symbol={summary.symbol}
        price={summary.current_price}
        percentChange={percentChange}
        valueChange={valueChange}
        valueChangeSuffix={` ${periodLabel}`}
        condensed={true}
      />
    </div>
  );
}

export function NewsSection({ content }: NewsSectionProps) {
  return (
    <div className="grid gap-20">
      <SectionHeader title={content.title} />
      {content.items.slice(0, MAX_NEWS_CARDS).map((item) => (
        <NewsCard
          key={item.id}
          title={item.title}
          content={item.content}
          links={item.links}
          dataTrack="home_news_card"
          dataTrackContext="news"
        >
          {item.ticker ? <NewsTickerSummary symbol={item.ticker} /> : null}
        </NewsCard>
      ))}
    </div>
  );
}
